import {
  type User as FirebaseUser,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithPopup,
} from "firebase/auth";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { firebaseAuth, googleProvider } from "../lib/firebase";
import { logger } from "../lib/logger";
import type { AuthContextValue, AuthState, User } from "../types/auth";

const API_BASE_URL =
  import.meta.env.VITE_EMPLOYEE_INTERVIEW_API_URL || "http://localhost:8000";

const MY_PROFILE_QUERY = `query MyProfile {
  myProfile {
    interviewerId
    email
    name
    isAdmin
  }
}`;

interface MyProfileResponse {
  errors?: unknown[];
  data?: {
    myProfile: {
      interviewerId: string;
      email: string | null;
      name: string | null;
      isAdmin: boolean;
    } | null;
  };
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  authErrorReason: null,
};

/**
 * プロフィール取得の結果。
 *
 * 「招待されていない」と「取得自体に失敗した」を呼び出し側で区別できるようにする。
 * どちらも null で返していたため、バックエンドの一時的な失敗が
 * 「あなたは登録されていません」と同じ扱いになり、原因が利用者に伝わらなかった。
 */
type ProfileLookupResult =
  | { kind: "found"; user: User }
  | { kind: "unregistered" }
  | { kind: "failed" };

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

async function fetchMyProfile(idToken: string): Promise<ProfileLookupResult> {
  // Auth 状態の確定に urql client を使うと AuthContext と循環依存になるため fetch を直接使う
  const response = await fetch(
    `${API_BASE_URL}/employee_interview/interviewers/graphql`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ query: MY_PROFILE_QUERY }),
    },
  );
  if (!response.ok) {
    // サーバー起因（5xx）は運用が気づくべき失敗、クライアント起因（4xx）は警告に留める
    const log = response.status >= 500 ? logger.error : logger.warn;
    log("myProfile lookup failed", { status: response.status });
    return { kind: "failed" };
  }
  const body = (await response.json()) as MyProfileResponse;
  if (body.errors && body.errors.length > 0) {
    // 原因の特定にエラーコードが要る。個人情報が乗らないよう message だけ拾う
    logger.error("myProfile lookup returned errors", {
      messages: body.errors
        .map((error) =>
          typeof error === "object" && error !== null && "message" in error
            ? String((error as { message: unknown }).message)
            : "unknown",
        )
        .join(" / "),
    });
    return { kind: "failed" };
  }
  const profile = body.data?.myProfile;
  if (!profile) return { kind: "unregistered" };
  return {
    kind: "found",
    user: {
      employeeInterviewInterviewerId: profile.interviewerId,
      email: profile.email ?? "",
      name: profile.name,
      isAdmin: profile.isAdmin,
    },
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);

  // 未招待アカウントを自分でサインアウトさせる間、Firebase から届く null 通知で
  // 表示中の理由が消えないようにする
  const signingOutUnregistered = useRef(false);

  useEffect(() => {
    return onAuthStateChanged(
      firebaseAuth,
      async (firebaseUser: FirebaseUser | null) => {
        if (!firebaseUser) {
          if (signingOutUnregistered.current) {
            signingOutUnregistered.current = false;
            return;
          }
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            authErrorReason: null,
          });
          return;
        }

        try {
          const idToken = await firebaseUser.getIdToken();
          const result = await fetchMyProfile(idToken);
          switch (result.kind) {
            case "found":
              setState({
                user: result.user,
                isAuthenticated: true,
                isLoading: false,
                authErrorReason: null,
              });
              return;
            case "unregistered":
              // 招待されていないアカウントは Firebase 側もサインアウトして入口に戻す
              signingOutUnregistered.current = true;
              await firebaseSignOut(firebaseAuth);
              setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                authErrorReason: "unregistered",
              });
              return;
            case "failed":
              // 取得に失敗しただけなのでサインアウトしない。原因が解消すれば再試行で入れる
              setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                authErrorReason: "lookupFailed",
              });
              return;
            default: {
              const unreachable: never = result;
              throw new Error(
                `unhandled profile lookup result: ${unreachable}`,
              );
            }
          }
        } catch (error) {
          logger.error(
            "Failed to resolve interviewer profile after Firebase sign-in",
            {
              error: error instanceof Error ? error.message : String(error),
            },
          );
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            authErrorReason: "lookupFailed",
          });
        }
      },
    );
  }, []);

  const signInWithGoogle = useCallback(async () => {
    // 試行の開始をここで表す。失敗理由も消しておかないと、同じ理由が続いたときに
    // 画面側が「状態が変わっていない」と判断してローディングを解けなくなる
    setState((prev) => ({ ...prev, authErrorReason: null, isLoading: true }));
    try {
      await signInWithPopup(firebaseAuth, googleProvider);
    } catch (error) {
      // ポップアップの失敗はここで確定する（onAuthStateChanged は発火しない）
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(firebaseAuth);
  }, []);

  const getIdToken = useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signInWithGoogle,
      signOut,
      getIdToken,
    }),
    [state, signInWithGoogle, signOut, getIdToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
